import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { makeStyles, Theme } from '@material-ui/core';
import MastercodeUnlockAnnouncement from 'components/@common/Announcements/MastercodeUnlockAnnouncement';
import UserClassesInformation from 'components/@common/UserProfile/UserClassesInformation';
import PageContainer from 'components/@layout/PageContainer';
import EndOfYearProcessingPanel from 'components/EndOfYearProcessing/components/EndOfYearProcessingPanel';
import { EndOfYearProcessingNotificationType } from 'components/EndOfYearProcessing/types';
import Config from 'config';
import GET_MY_ORGANIZATION_NAMES_CLASSROOMS_AND_MASTER_CODES from 'graph/composite/get-my-organization-names-classrooms-and-master-codes-query';
import { flow, filter, uniqBy } from 'lodash/fp';
import useStore from 'store';
import { PliManagedApp } from 'store/cache/product-cache';
import { ModuleProps } from 'types';
import { Product, ProductType, UserOrganization } from 'types/schema-generated-types';
import { proxyInsufficientPrivilegesError } from 'utilities/proxy-apollo-error';
import HeaderBar from '../HeaderBar';
import TeacherApplicationInformation from './components/TeacherApplicationInformation';
import UserInformation from './components/UserInformation';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: `${theme.spacing(3)}px ${theme.spacing(5)}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: `${theme.spacing(4)}px`
  },
  userClassesContainer: {
    gridArea: '3 / 1 / 4 / 4',
    marginTop: theme.spacing(5)
  }
}));

const { appTileOverrides } = Config;

const Dashboard: React.FC<ModuleProps> = props => {
  const classes = useStyles();
  const { t } = useTranslation('Dashboard');
  const [
    {
      user: { me },
      productCache
    }
  ] = useStore();

  const { data, loading, error } = useQuery(GET_MY_ORGANIZATION_NAMES_CLASSROOMS_AND_MASTER_CODES, {
    variables: { userId: me?.id },
    errorPolicy: 'all'
  });

  const getOrganizations = React.useCallback((): string => {
    return data?.user.organizationAssociations
      .map(({ organization }: UserOrganization) => organization.name)
      .join(', ');
  }, [data]);

  const apps = React.useMemo(() => {
    if (!me?.licenseUsage) return [];

    const activeProducts = me.licenseUsage.reduce((acc, licenseUsage) => {
      if (licenseUsage.product?.type !== ProductType.USER) return acc;
      if (!licenseUsage.assignedCount) return acc;
      if (!licenseUsage.product?.code) return acc;

      return acc.set(licenseUsage.product.code, licenseUsage.product);
    }, new Map<string, Product>());

    const result: PliManagedApp[] = [];
    activeProducts.forEach(product => {
      const productApp = productCache.findAppByProductCode(product.code);
      if (productApp) result.push(productApp);
    });

    return flow(
      filter((pliManagedApp: PliManagedApp) => {
        const override = appTileOverrides.find(override => override.app === pliManagedApp.app.name);
        return !override || !result.some(a => pliManagedApp.app.name === override.supppressingApp);
      }),
      uniqBy(
        (pliManagedApp: PliManagedApp) =>
          `${pliManagedApp.app.id}${
            pliManagedApp.appProduct?.id ? `-${pliManagedApp.appProduct.id}` : ''
          }`
      )
    )(result);
  }, [me, productCache]);

  const organizations = getOrganizations();
  const classrooms = data?.classrooms;
  const classroomsCount = data?.classrooms?.length || 0;

  return (
    <PageContainer loading={loading} error={proxyInsufficientPrivilegesError(error)}>
      <HeaderBar {...props} />
      <div className={classes.container}>
        {me && data?.user && (
          <UserInformation
            firstName={me?.firstName}
            lastName={me?.lastName}
            avatarUrl={me?.avatarUrl}
            username={me?.username}
            pin={me?.pin}
            type={data?.user.type}
            organizations={organizations}
          />
        )}
        <EndOfYearProcessingPanel
          hideTypes={[EndOfYearProcessingNotificationType.NEW_YEAR_IMPLEMENTED]}
        />
        <MastercodeUnlockAnnouncement href="/dashboard/orders" mastercodes={data?.mastercodes} />
        <div>
          {apps.length > 0 && <TeacherApplicationInformation apps={apps} />}
          {data?.classrooms && classroomsCount > 0 && (
            <div className={classes.userClassesContainer}>
              <UserClassesInformation
                classrooms={classrooms}
                emptyMessage={t('You have no active classes.')}
                btnText={'View My Classes & Students'}
              />
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default Dashboard;
